import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  FC,
  ReactNode,
} from "react";
import { User } from "oidc-client-ts";
import { userManager } from "./oidcClient";
import { AddInStorage } from "../storage/AddInStorage";
import { refreshMSALToken } from "../util-helpers/msal";
import {
  generateHeaders,
  initializeClientIP,
  getOrgId,
  getUserId,
} from "./userSession";
import trackOutlookEnvironment from "../util-helpers/outlookEnvironment";
import { isNullOrUndefined } from "../util-helpers/common";
import { nrNoticeError } from "../util-helpers/newrelic";
import { pendoInitialize } from "../util-helpers/pendo";

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isMsalLoading: boolean;
  validatedMSAL: boolean;
  authError: string | null;
  contextSignin: (user: User, newSignin?: boolean) => void;
  contextSignout: () => void;
  resetMSAL: () => void;
}

const isMSALPopupRoute = window.location.pathname === "/msalauth"
  || window.location.pathname === "/msalrefresh"
  || window.location.pathname === "/msallogout"
  || window.location.pathname === "/msallogoutsuccess";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMsalLoading, setIsMsalLoading] = useState(false);
  const [validatedMSAL, setValidatedMSAL] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    async function loadUser() {
      try {
        // Initialize client IP for request headers
        initializeClientIP().catch((error) =>
          console.log("Failed to initialize client IP:", error));

        const existingUser = await userManager.getUser();
        if (existingUser && !existingUser.expired) {
          contextSignin(existingUser);
        } else {
          // If no user is found or the user is expired, try to sign in silently
          try {
            const silentUser = await userManager.signinSilent();
            if (silentUser) {
              contextSignin(silentUser);
            }
          } catch (silentError) {
            const errorMessage = silentError instanceof Error
                ? silentError.message
                : String(silentError);

            if (errorMessage.includes("interaction_required")) {
              console.log("Silent renew failed: User interaction required.");
            } else if (errorMessage.includes("login_required")) {
              console.log(
                "Silent renew failed: Login required. User session has fully expired."
              );
            } else {
              console.log(
                "Silent renew failed with unexpected error:",
                silentError
              );
            }

            // Clear any stale user data to ensure clean state
            try {
              await userManager.removeUser();
            } catch (cleanupError) {
              console.error("Error cleaning up user data:", cleanupError);
            }
          }
        }
      } catch (error) {
        console.log("Could not automatically sign in user.", error);
      } finally {
        setIsLoading(false);
      }
    }

    const handleSilentRenewError = (error: Error) => {
      console.log("Automatic silent renew failed:", error.message);
      contextSignout();
    };

    const handleUserSignedOut = () => {
      console.log("User signed out event received");
      contextSignout();
    };

    // Add event listeners
    if (userManager.events) {
      userManager.events.addSilentRenewError(handleSilentRenewError);
      userManager.events.addUserSignedOut(handleUserSignedOut);
    }

    loadUser();

    // Cleanup event listeners on unmount
    return () => {
      if (userManager.events) {
        userManager.events.removeSilentRenewError(handleSilentRenewError);
        userManager.events.removeUserSignedOut(handleUserSignedOut);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tryValidateMSAL = async () => {
    if (window.location.pathname !== "/auth" && !isMSALPopupRoute) {
      try {
        setIsMsalLoading(true);
        setValidatedMSAL(await refreshMSALToken());
      } catch (error) {
        console.error("Error validating MSAL token:", error);
        setValidatedMSAL(false);
      } finally {
        setIsMsalLoading(false);

        trackOutlookEnvironment();
      }
    } else {
      setValidatedMSAL(false);
    }
  };

  const contextSignin = async (userToLogin: User, newSignin = false) => {
    // Always ensure org data is loaded when signing in
    // Check if orgId/userId are missing - if so, we need to fetch them
    const needsOrgData = isNullOrUndefined(getOrgId()) || !getUserId();

    if (needsOrgData) {
      try {
        await generateHeaders(true);
        if (newSignin) {
          AddInStorage.persistData();
        }
        setAuthError(null); // Clear any previous errors
      } catch (error) {
        const axiosError = error as any;
        const status = axiosError?.response?.status;
        const hasRequest = !!axiosError?.request;
        const hasResponse = !!axiosError?.response;
        // Network/CORS errors have request but no response
        const isNetworkError = hasRequest && !hasResponse;

        // Log to New Relic
        nrNoticeError(
          error instanceof Error
            ? error
            : new Error("Failed to fetch user organizations"),
          {
            context: "contextSignin",
            status: status || 0,
            isNetworkError: isNetworkError ? "true" : "false",
            url: window.location.href,
          }
        );

        // Determine appropriate error message
        let errorMessage: string;
        // Status 0 indicates network/CORS error, 503/504 are service errors
        if (
          status === 0
          || status === 503
          || status === 504
          || isNetworkError
        ) {
          // Treat status 0, 503/504 and network errors (including CORS failures) as service unavailable
          errorMessage = "The Filevine service is temporarily unavailable.";
        } else {
          errorMessage = "Failed to load user organization data.";
        }

        setAuthError(errorMessage);
        // Still set user so they're not stuck in loading
        setUser(userToLogin);
        setIsLoading(false);
        return;
      }
    }

    setUser(userToLogin);
    tryValidateMSAL();

    // Initialize Pendo after user is authenticated and org data is loaded
    try {
      pendoInitialize(userToLogin);
    } catch (error) {
      console.error("Pendo initialization error:", error);
      // Don't block auth flow if Pendo fails
    }
  };

  const contextSignout = () => {
    setUser(null);
    setAuthError(null);
    AddInStorage.clear();
  };

  const resetMSAL = () => {
    setValidatedMSAL(false);
    setIsMsalLoading(false);
  };

  const value = {
    user,
    isLoading,
    isMsalLoading,
    validatedMSAL,
    authError,
    contextSignin,
    contextSignout,
    resetMSAL,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
